import React from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import ReactDOMServer from 'react-dom/server';
import { MemoryRouter } from 'react-router-dom';
import InvoiceDisplay from './InvoiceDisplay';

interface InvoicePDFGeneratorProps {
  invoiceHtml: string;
  invoiceNumber: string;
}

export const generateInvoicePDF = async (
  invoiceHtml: string,
  invoiceNumber: string
): Promise<string> => {
  try {
    const pdfBlob = await generateInvoicePDFBlob(invoiceHtml);

    // Create a reference to the storage location
    const storageRef = ref(storage, `invoices/${invoiceNumber}.pdf`);

    // Upload the PDF
    await uploadBytes(storageRef, pdfBlob);

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

const LOCAL_LOGO_PATH = '/images/berwick-logo.png';

const waitForImages = (container: HTMLElement): Promise<void> => {
  const images = Array.from(container.querySelectorAll('img'));
  return Promise.all(
    images.map((img) => {
      if (img.src.includes('berwick-logo.png')) {
        img.src = `${window.location.origin}${LOCAL_LOGO_PATH}`;
      }

      if (img.complete && img.naturalHeight > 0) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    })
  ).then(() => undefined);
};

export const generateInvoicePDFBlob = async (
  invoiceHtml: string
): Promise<Blob> => {
  try {
    // Create a temporary div to render the invoice HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = invoiceHtml;
    tempDiv.style.width = '816px'; // US Letter width at 96dpi
    tempDiv.style.height = '1056px'; // US Letter height at 96dpi
    tempDiv.style.background = 'white'; // Ensure white background
    document.body.appendChild(tempDiv);

    tempDiv.querySelector('.print-button')?.remove();
    await waitForImages(tempDiv);

    const paymentAnchor = tempDiv.querySelector('a[href*="/pay/"]') as HTMLAnchorElement | null;
    let paymentUrl: string | null = null;
    if (paymentAnchor) {
      const href = paymentAnchor.getAttribute('href') || '';
      paymentUrl = href.startsWith('http')
        ? href
        : `https://berwickregistration.web.app${href.startsWith('/') ? '' : '/'}${href}`;

      const paymentListItem = Array.from(tempDiv.querySelectorAll('li')).find((item) =>
        item.textContent?.includes('Credit card:')
      );
      if (paymentListItem) {
        paymentListItem.innerHTML = '<strong>Credit card:</strong> use the secure payment page below';
      }

      const replacement = document.createElement('p');
      replacement.id = 'pdf-payment-link';
      replacement.textContent = paymentUrl;
      replacement.style.margin = '20px 0 0 0';
      replacement.style.textAlign = 'center';
      replacement.style.fontSize = '14px';
      replacement.style.fontWeight = '600';
      replacement.style.color = '#1a73e8';
      replacement.style.textDecoration = 'underline';
      replacement.style.wordBreak = 'break-all';

      const buttonWrapper = paymentAnchor.parentElement;
      if (buttonWrapper) {
        buttonWrapper.replaceChildren(replacement);
      } else {
        paymentAnchor.replaceWith(replacement);
      }
    }

    const containerRect = tempDiv.getBoundingClientRect();
    const paymentLinkElement = tempDiv.querySelector('#pdf-payment-link') as HTMLElement | null;
    const paymentLinkRect = paymentLinkElement?.getBoundingClientRect() || null;

    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // Higher scale for better quality
      useCORS: true, // Enable CORS for images
      logging: false, // Disable logging
    });

    // Remove the temporary div
    document.body.removeChild(tempDiv);

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter', // US Letter size
    });

    const imgWidth = 216; // mm (US Letter width)
    const imgHeight = 279; // mm (US Letter height)

    // Add the image to the PDF
    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0,
      0,
      imgWidth,
      imgHeight
    );

    if (paymentUrl && paymentLinkRect) {
      const x = ((paymentLinkRect.left - containerRect.left) / containerRect.width) * imgWidth;
      const y = ((paymentLinkRect.top - containerRect.top) / containerRect.height) * imgHeight;
      const width = (paymentLinkRect.width / containerRect.width) * imgWidth;
      const height = (paymentLinkRect.height / containerRect.height) * imgHeight;

      pdf.link(x, y, width, height, { url: paymentUrl });
    }

    // Convert PDF to blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Render an InvoiceDisplay model to a PDF blob using the exact same rendering
// pipeline as the on-screen invoice. Wraps in MemoryRouter so InvoiceDisplay's
// useNavigate hook resolves during static rendering. This lets batch consumers
// (e.g. AR reminders) attach an identical invoice PDF without mounting the
// component in the live React tree.
export const generateInvoicePDFBlobFromModel = async (
  model: any,
  invoiceNumber: string,
  invoiceDate?: Date | string | null
): Promise<Blob> => {
  const html = ReactDOMServer.renderToStaticMarkup(
    <MemoryRouter>
      <InvoiceDisplay registration={model} invoiceNumber={invoiceNumber} invoiceDate={invoiceDate} />
    </MemoryRouter>
  );
  return generateInvoicePDFBlob(html);
};

export const deleteInvoicePDF = async (invoiceNumber: string): Promise<void> => {
  try {
    const storageRef = ref(storage, `invoices/${invoiceNumber}.pdf`);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting PDF:', error);
    throw error;
  }
};

const InvoicePDFGenerator: React.FC<InvoicePDFGeneratorProps> = () => {
  // This component is just a utility component, no UI needed
  return null;
};

export default InvoicePDFGenerator;

export function generateInvoiceHtml(registration: any, financialRecord: any): string {
  // Map data to match InvoiceDisplayProps
  const invoiceNumber = financialRecord.invoiceNumber || registration.invoiceNumber || '';
  
  // Calculate section totals including overrides
  const accommodationFee = Number(financialRecord.accommodationFees?.fee) || 0;
  const accommodationOverride = Number(financialRecord.accommodationFees?.override?.amount) || 0;
  const accommodationTotal = accommodationFee + accommodationOverride;

  const registrationBaseTotal = (financialRecord.registrationFees || []).reduce(
    (sum: number, fee: any) => sum + (Number(fee.fee) || 0), 0
  );
  const registrationOverride = Number(financialRecord.registrationOverride?.amount) || 0;
  const registrationTotal = registrationBaseTotal + registrationOverride;

  const fridgeRental = Number(financialRecord.otherFees?.fridgeRental) || Number(financialRecord.otherFees?.fridge?.cost) || 0;
  const donation = Number(financialRecord.otherFees?.donation) || Number(financialRecord.otherFees?.donations?.amount) || 0;
  const donationNote = financialRecord.otherFees?.donationNote || financialRecord.otherFees?.donations?.note || '';
  const customFee = Number(financialRecord.otherFees?.customFee) || Number((financialRecord.otherFees?.customFee as any)?.amount) || 0;
  const customFeeNote = financialRecord.otherFees?.customFeeNote || (financialRecord.otherFees?.customFee as any)?.note || '';
  const otherFeesArrayTotal = (financialRecord.otherFees?.otherFees || []).reduce((sum: number, fee: any) => sum + (Number(fee.amount) || 0), 0);
  const otherFeesOverride = Number(financialRecord.otherFees?.override?.amount) || 0;
  const otherFeesBaseTotal = fridgeRental + donation + customFee + otherFeesArrayTotal;
  const otherFeesTotal = otherFeesBaseTotal + otherFeesOverride;

  const subtotal = accommodationTotal + registrationTotal + otherFeesTotal;
  const paymentAmount = Number(financialRecord.paymentAmount) || 0;
  const balanceDue = subtotal - paymentAmount;

  const invoiceProps = {
    registration: {
      id: registration.id,
      primaryCamper: {
        firstName: registration.firstName || '',
        lastName: registration.lastName || '',
        email: registration.email || '',
        address: registration.address || '',
        city: registration.city || '',
        province: registration.province || '',
        postalCode: registration.postalCode || '',
      },
      accommodationFees: {
        ...financialRecord.accommodationFees,
        numberOfNights: financialRecord.accommodationFees?.numberOfNights || 0,
      },
      registrationFees: financialRecord.registrationFees,
      registrationOverride: financialRecord.registrationOverride,
      otherFees: financialRecord.otherFees,
      totalAmount: subtotal,
      paymentAmount: paymentAmount,
      remainingBalance: balanceDue,
    },
    invoiceNumber,
  };
  return ReactDOMServer.renderToStaticMarkup(
    <InvoiceDisplay {...invoiceProps} />
  );
}

export function generateEmailInvoiceHtml(registration: any, financialRecord: any): string {
  // Map data to match InvoiceDisplayProps
  const invoiceNumber = financialRecord.invoiceNumber || registration.invoiceNumber || '';
  
  // Calculate section totals including overrides
  const accommodationFee = Number(financialRecord.accommodationFees?.fee) || 0;
  const accommodationOverride = Number(financialRecord.accommodationFees?.override?.amount) || 0;
  const accommodationTotal = accommodationFee + accommodationOverride;

  const registrationBaseTotal = (financialRecord.registrationFees || []).reduce(
    (sum: number, fee: any) => sum + (Number(fee.fee) || 0), 0
  );
  const registrationOverride = Number(financialRecord.registrationOverride?.amount) || 0;
  const registrationTotal = registrationBaseTotal + registrationOverride;

  const fridgeRental = Number(financialRecord.otherFees?.fridgeRental) || Number(financialRecord.otherFees?.fridge?.cost) || 0;
  const donation = Number(financialRecord.otherFees?.donation) || Number(financialRecord.otherFees?.donations?.amount) || 0;
  const donationNote = financialRecord.otherFees?.donationNote || financialRecord.otherFees?.donations?.note || '';
  const customFee = Number(financialRecord.otherFees?.customFee) || Number((financialRecord.otherFees?.customFee as any)?.amount) || 0;
  const customFeeNote = financialRecord.otherFees?.customFeeNote || (financialRecord.otherFees?.customFee as any)?.note || '';
  const otherFeesArrayTotal = (financialRecord.otherFees?.otherFees || []).reduce((sum: number, fee: any) => sum + (Number(fee.amount) || 0), 0);
  const otherFeesOverride = Number(financialRecord.otherFees?.override?.amount) || 0;
  const otherFeesBaseTotal = fridgeRental + donation + customFee + otherFeesArrayTotal;
  const otherFeesTotal = otherFeesBaseTotal + otherFeesOverride;

  const subtotal = accommodationTotal + registrationTotal + otherFeesTotal;
  const paymentAmount = Number(financialRecord.paymentAmount) || 0;
  const balanceDue = subtotal - paymentAmount;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  return `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
      <!-- Header without logo -->
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2c3e50; padding-bottom: 20px;">
        <h1 style="color: #2c3e50; margin: 0;">UCCMA</h1>
        <h2 style="color: #2c3e50; margin: 5px 0 0 0; font-size: 1.2em;">United Church Camp Meeting Association</h2>
        <h2 style="color: #2c3e50; margin: 20px 0 0 0;">Invoice</h2>
        <p style="margin: 5px 0;"><strong>Invoice #:</strong> ${invoiceNumber}</p>
        <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
      </div>

      <!-- Billing Information -->
      <div style="margin-bottom: 30px;">
        <h3 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px;">Bill To:</h3>
        <p style="margin: 5px 0;">
          ${registration.firstName || ''} ${registration.lastName || ''}
          ${registration.address ? `, ${registration.address}` : ''}
          ${registration.city ? `, ${registration.city}` : ''}
          ${registration.province ? `, ${registration.province}` : ''}
          ${registration.postalCode ? ` ${registration.postalCode}` : ''}
        </p>
        <p style="margin: 5px 0;">${registration.email || ''}</p>
      </div>

      <!-- Fees Sections -->
      <div style="margin-bottom: 30px;">
        <h3 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px;">Accommodation Fees:</h3>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tbody>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${financialRecord.accommodationFees?.placeName || ''} - ${financialRecord.accommodationFees?.numberOfNights || 0} nights</td>
              <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(financialRecord.accommodationFees?.fee || 0)}</td>
            </tr>
            ${financialRecord.accommodationFees?.override ? `
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #eee;">Accommodation Override${financialRecord.accommodationFees.override.note ? ` (${financialRecord.accommodationFees.override.note})` : ''}</td>
                <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(financialRecord.accommodationFees.override.amount)}</td>
              </tr>
            ` : ''}
          </tbody>
        </table>
        <p style="text-align: right; font-weight: bold;">Accommodation Total: ${formatCurrency(accommodationTotal)}</p>
      </div>

      ${(financialRecord.registrationFees || []).length > 0 ? `
        <div style="margin-bottom: 30px;">
          <h3 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px;">Registration Fees:</h3>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <tbody>
              ${(financialRecord.registrationFees || []).map((fee: any) => `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">${fee.name || ''} - ${fee.category || ''} (${fee.nights || 0} nights)</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(fee.fee || 0)}</td>
                </tr>
              `).join('')}
              ${financialRecord.registrationOverride ? `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">Registration Override${financialRecord.registrationOverride.note ? ` (${financialRecord.registrationOverride.note})` : ''}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(financialRecord.registrationOverride.amount)}</td>
                </tr>
              ` : ''}
            </tbody>
          </table>
          <p style="text-align: right; font-weight: bold;">Registration Total: ${formatCurrency(registrationTotal)}</p>
        </div>
      ` : ''}

      ${(fridgeRental > 0 || donation > 0 || otherFeesArrayTotal > 0 || otherFeesOverride > 0) ? `
        <div style="margin-bottom: 30px;">
          <h3 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px;">Other Fees:</h3>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <tbody>
              ${fridgeRental > 0 ? `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">Fridge Rental</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(fridgeRental)}</td>
                </tr>
              ` : ''}
              ${donation > 0 ? `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">Donation${donationNote ? ` <span style="font-style: italic; color: #666;">(${donationNote})</span>` : ''}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(donation)}</td>
                </tr>
              ` : ''}
              ${customFee > 0 ? `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">Other Fees${customFeeNote ? ` <span style="font-style: italic; color: #666;">(${customFeeNote})</span>` : ''}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(customFee)}</td>
                </tr>
              ` : ''}
              ${(financialRecord.otherFees?.otherFees || []).map((fee: any) => `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">${fee.note || 'Other Fee'}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(fee.amount || 0)}</td>
                </tr>
              `).join('')}
              ${otherFeesOverride > 0 ? `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">Other Fees Override${financialRecord.otherFees?.override?.note ? ` (${financialRecord.otherFees.override.note})` : ''}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(otherFeesOverride)}</td>
                </tr>
              ` : ''}
            </tbody>
          </table>
          <p style="text-align: right; font-weight: bold;">Other Fees Total: ${formatCurrency(otherFeesTotal)}</p>
        </div>
      ` : ''}

      <!-- Summary -->
      <div style="margin-bottom: 30px; border-top: 2px solid #2c3e50; padding-top: 20px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tbody>
            <tr>
              <td style="padding: 8px; text-align: right; font-weight: bold;">Subtotal:</td>
              <td style="padding: 8px; text-align: right; font-weight: bold;">${formatCurrency(subtotal)}</td>
            </tr>
            ${paymentAmount > 0 ? `
              <tr>
                <td style="padding: 8px; text-align: right;">Payments Made:</td>
                <td style="padding: 8px; text-align: right;">-${formatCurrency(paymentAmount)}</td>
              </tr>
            ` : ''}
            <tr style="background-color: #f8f9fa;">
              <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 1.1em;">Balance Due:</td>
              <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 1.1em; color: #dc3545;">${formatCurrency(balanceDue)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
} 